import React, { useEffect, useState } from "react";
import { api, formatApiError, downloadFile } from "../lib/api";
import { inr, todayIso, prettyDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { FileSpreadsheet, FileText, Filter, BookMarked, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function firstOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

export default function Reports() {
  const navigate = useNavigate();
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayIso());
  const [cat, setCat] = useState([]);
  const [cs, setCS] = useState([]);
  const [cv, setCV] = useState([]);
  const [pers, setPers] = useState([]);
  const [pending, setPending] = useState({ persons: [], total_pending: 0 });
  const [loading, setLoading] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = { from_date: from, to_date: to };
      const [c, s, v, p, pp] = await Promise.all([
        api.get("/reports/category-summary", { params }),
        api.get("/reports/cs", { params }),
        api.get("/reports/cv", { params }),
        api.get("/reports/person-suspense", { params }),
        api.get("/suspense/summary"),
      ]);
      setCat(c.data); setCS(s.data); setCV(v.data); setPers(p.data); setPending(pp.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const download = async (path, name) => {
    try { await downloadFile(path, { from_date: from, to_date: to }, name); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="label-eyebrow">Reports</div>
        <h1 className="font-heading text-4xl font-bold mt-1">Reports & Exports</h1>
      </div>

      <Card className="p-5 border border-line">
        <div className="flex items-end gap-3 flex-wrap">
          <div><Label className="label-eyebrow">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1.5 font-mono" data-testid="r-from" /></div>
          <div><Label className="label-eyebrow">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1.5 font-mono" data-testid="r-to" /></div>
          <Button onClick={fetchAll} disabled={loading} data-testid="r-apply" className="bg-ink text-white"><Filter size={14} className="mr-1.5" /> Apply</Button>
          <div className="ml-auto flex gap-2 flex-wrap">
            <Button onClick={() => navigate("/journal")} variant="outline" data-testid="goto-journal-btn"><BookMarked size={14} className="mr-1.5" /> Open Full Accounts Journal</Button>
            <Button onClick={() => navigate("/reports/petty-cash")} variant="outline" data-testid="goto-pettycash-btn"><Wallet size={14} className="mr-1.5" /> Open Petty Cash Report</Button>
            <Button variant="outline" onClick={() => download("/reports/cashbook.xlsx", `cashbook_${from}_${to}.xlsx`)} data-testid="export-xlsx-btn"><FileSpreadsheet size={14} className="mr-1.5" /> Excel</Button>
            <Button variant="outline" onClick={() => download("/reports/cashbook.pdf", `cashbook_${from}_${to}.pdf`)} data-testid="export-pdf-btn"><FileText size={14} className="mr-1.5" /> PDF</Button>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="category">
        <TabsList className="bg-paper-alt">
          <TabsTrigger value="category" data-testid="tab-category">Category Summary</TabsTrigger>
          <TabsTrigger value="cs" data-testid="tab-cs">Date-wise CS</TabsTrigger>
          <TabsTrigger value="cv" data-testid="tab-cv">Date-wise CV &amp; SA</TabsTrigger>
          <TabsTrigger value="persons" data-testid="tab-persons">Person Suspense</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending Suspense</TabsTrigger>
        </TabsList>

        <TabsContent value="category">
          <SimpleTable
            cols={["Category", "Entries", "Cash In", "Cash Out", "Expense"]}
            rows={cat.map(r => [r.name, r.count, r.in ? inr(r.in) : "—", r.out ? inr(r.out) : "—", r.expense ? inr(r.expense) : "—"])}
            rightCols={[2, 3, 4]} testid="category-rows"
          />
        </TabsContent>
        <TabsContent value="cs">
          <SimpleTable
            cols={["Date", "CS No", "Particulars", "Category", "Amount", "By"]}
            rows={cs.map(r => [r.date, r.doc_no, r.particulars, r.category_name || "—", inr(r.amount), r.created_by_name])}
            rightCols={[4]} testid="cs-rows"
          />
        </TabsContent>
        <TabsContent value="cv">
          <SimpleTable
            cols={["Date", "Doc No", "Type", "Person", "Particulars", "Amount", "By"]}
            rows={cv.map(r => [r.date, r.doc_no, r.entry_type === "cv" ? "Direct" : r.entry_type === "sa" ? "Advance" : "Bill", r.person_name || "—", r.particulars, inr(r.amount), r.created_by_name])}
            rightCols={[5]} testid="cv-rows"
          />
        </TabsContent>
        <TabsContent value="persons">
          <SimpleTable
            cols={["Person", "Advanced", "Settled", "Net", "Entries"]}
            rows={pers.map(r => [r.person_name, inr(r.advanced), inr(r.settled), inr(r.net), r.count])}
            rightCols={[1, 2, 3]} testid="person-suspense-rows"
          />
        </TabsContent>
        <TabsContent value="pending">
          <Card className="p-4 mb-4 border border-line overflow-hidden">
            <div className="label-eyebrow">Total Pending</div>
            <div className="mt-1 font-mono tabular-nums whitespace-nowrap text-xl sm:text-2xl font-bold text-warn">{inr(pending.total_pending)}</div>
          </Card>
          <SimpleTable
            cols={["Person", "Advanced", "Settled", "Pending"]}
            rows={pending.persons.map(r => [r.person_name, inr(r.advanced), inr(r.settled), inr(r.pending)])}
            rightCols={[1, 2, 3]} testid="pending-rows"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SimpleTable({ cols, rows, rightCols = [], testid }) {
  return (
    <Card className="overflow-hidden border border-line mt-4">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-paper-alt border-b border-line">
            {cols.map((c, i) => <th key={i} className={`px-4 py-3 label-eyebrow whitespace-nowrap ${rightCols.includes(i) ? "text-right" : "text-left"}`}>{c}</th>)}
          </tr></thead>
          <tbody data-testid={testid}>
            {rows.length === 0 ? <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-muted">No data.</td></tr> :
              rows.map((r, i) => (
                <tr key={i} className="border-b border-line hover:bg-paper/60">
                  {r.map((v, j) => <td key={j} className={`px-4 py-2.5 whitespace-nowrap ${rightCols.includes(j) ? "text-right font-mono" : ""}`}>{v}</td>)}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
